import report from '../report.js';
import {
  loadImage,
  base64ToFile,
  calcMaxSize,
  blobUrlToFile,
  logTime,
  formatLogTime,
  formatFileSize,
  drawImageWithOrientationToCanvas
} from './imageHelpers.js';
import { isDev } from '../config.js';

/**
 * 仅在开发环境输出 compressImage 调试日志
 *
 * @param {...any} args - 需要输出到控制台的日志参数
 * @returns {void}
 */
function logCompressImageDebug(...args) {
  // 仅开发环境输出临时调试日志
  if (isDev) {
    console.log(...args);
  }
}

/**
 * 将图片压到指定最大边长、可选 EXIF 方向修正，并按体积递归降低质量/尺寸，返回 Blob URL（部分分支原样返回 data/blob 字符串）。
 *
 * @param {string|File} imageSource - 图片来源：URL、blob URL、data URL 字符串，或 File（与 loadImage 一致）
 * @param {Object} [options] - 压缩与行为选项
 * @param {number} [options.maxWidthOrHeight] - 长边上限（像素）。未传时在函数内会按 5000 参与缩放计算
 * @param {number} [options.quality=1] - canvas.toBlob 质量系数 0–1，体积超限递归时会乘以 0.9
 * @param {number} [options.maxSizeMB=5] - 输出 blob 目标体积上限（MB）；超过则最多再压 3 次（含递归）
 * @param {number} [options.repeatTime=0] - 当前递归轮次，一般由内部递增，调用方可不传
 * @param {number} [options.orientation=1] - EXIF orientation 1–8；非 1 时会走画布变换
 * @param {string} [options.customImageType] - 多次压缩时可能被设为 'image/jpeg'（历史字段）。注意：toBlob 的 MIME 仍只由 originImageType 映射决定，本字段不参与 toBlob（与历史行为一致）
 * @param {boolean} [options.keepOriginType=false] - 为 true 时，repeatTime>1 时不会写入 customImageType
 * @param {boolean} [options.autoRotateImage=false] - 为 true 且 orientation 为 1 时，按模板比例与横竖图自动改为 orientation=6
 * @param {number} [options.templateImageRatio=0.5] - 模板宽高比（宽/高），配合 autoRotateImage
 * @param {boolean} [options.keepWidthAndHeight=false] - 为 true 时超限只降 quality，不缩小 maxWidthOrHeight
 * @param {string} [options.originImageType] - 'png'|'jpg'|'jpeg'，映射为 toBlob 的 MIME；缺省为 png
 * @returns {Promise<string>} 通常为 blob: URL；base64/blob 源在体积与方向均无需处理时可能直接返回原 imageSource 字符串
 */
export async function compressImage(imageSource, options) {
  options = options || {};

  // ---------- 1. 解析 options（默认值与历史字段） ----------
  var maxWidthOrHeight = options.maxWidthOrHeight;
  var quality = options.quality === undefined ? 1 : options.quality;
  var maxSizeMB = options.maxSizeMB === undefined ? 5 : options.maxSizeMB;
  var repeatTime = options.repeatTime === undefined ? 0 : options.repeatTime;
  var orientation = options.orientation === undefined ? 1 : options.orientation;
  var keepOriginType =
    options.keepOriginType === undefined ? false : options.keepOriginType;
  var autoRotateImage =
    options.autoRotateImage === undefined ? false : options.autoRotateImage;
  var templateImageRatio =
    options.templateImageRatio === undefined ? 0.5 : options.templateImageRatio;
  var keepWidthAndHeight =
    options.keepWidthAndHeight === undefined
      ? false
      : options.keepWidthAndHeight;

  var time = +new Date();
  logCompressImageDebug('[compressImage] start', {
    sourceType: imageSource instanceof File ? 'file' : typeof imageSource,
    isBase64Url: typeof imageSource === 'string' && /^data:/.test(imageSource),
    isBlobUrl: typeof imageSource === 'string' && /^blob:/.test(imageSource),
    maxWidthOrHeight,
    quality,
    maxSizeMB,
    repeatTime,
    orientation,
    keepOriginType,
    autoRotateImage,
    templateImageRatio,
    keepWidthAndHeight
  });

  // ---------- 2. 加载图片，判断输入形态 ----------
  logCompressImageDebug('[compressImage] before loadImage', {
    cost: new Date() - time
  });
  var img = await loadImage(imageSource);
  logCompressImageDebug('[compressImage] after loadImage', {
    cost: new Date() - time,
    naturalWidth: img.naturalWidth,
    naturalHeight: img.naturalHeight
  });
  var isBase64Url = /^data:/.test(imageSource);
  var isBlobUrl = /^blob:/.test(imageSource);
  var rotate = 0;
  var naturalWidth = img.naturalWidth;
  var naturalHeight = img.naturalHeight;

  // ---------- 3. 可选：按模板比例自动旋转（仅原 orientation 为 1 时） ----------
  if (autoRotateImage && templateImageRatio && orientation == 1) {
    if (
      (naturalWidth > naturalHeight && templateImageRatio < 1) ||
      (naturalWidth < naturalHeight && templateImageRatio > 1)
    ) {
      orientation = 6;
    }
  }

  // ---------- 4. 是否需要走 Canvas（缩放或方向修正） ----------
  var needCompress =
    maxWidthOrHeight &&
    (naturalWidth > maxWidthOrHeight || naturalHeight > maxWidthOrHeight);

  needCompress = needCompress || !!rotate || orientation != 1;

  // ---------- 5. 已是 data/blob 且体积与方向均无需处理则直接返回原串 ----------
  if (isBase64Url) {
    logCompressImageDebug('[compressImage] before base64ToFile', {
      cost: new Date() - time
    });
    var file0 = await base64ToFile(imageSource);
    logCompressImageDebug('[compressImage] after base64ToFile', {
      cost: new Date() - time,
      fileSize: file0.size,
      needCompress
    });
    // 与历史实现一致：此处用 file 字节与 maxSizeMB 比较（调用方若传 MB 需注意）
    if (file0.size <= maxSizeMB && !needCompress) {
      logCompressImageDebug('[compressImage] return origin base64', {
        cost: new Date() - time,
        fileSize: file0.size
      });
      return imageSource;
    }
  }
  if (isBlobUrl) {
    logCompressImageDebug('[compressImage] before blobUrlToFile source', {
      cost: new Date() - time
    });
    var file1 = await blobUrlToFile(imageSource);
    var mb0 = file1.size / 1024 / 1024;
    logCompressImageDebug('[compressImage] after blobUrlToFile source', {
      cost: new Date() - time,
      fileSize: file1.size,
      mb: mb0,
      needCompress
    });
    if (mb0 <= maxSizeMB && !needCompress) {
      logCompressImageDebug('[compressImage] return origin blobUrl', {
        cost: new Date() - time,
        mb: mb0
      });
      return imageSource;
    }
  }

  // ---------- 6. 输出 MIME（仅 originImageType；customImageType 不参与 toBlob） ----------
  var imageTypeMap = {
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg'
  };

  var type = imageTypeMap[options.originImageType] || imageTypeMap.png;

  // ---------- 7. 计算绘制到画布上的逻辑宽高（等比限制在长边 maxWidthOrHeight 内） ----------
  var canvasWidth = naturalWidth;
  var canvasHeight = naturalHeight;

  if (!maxWidthOrHeight) {
    maxWidthOrHeight = 5000;
  }
  if (naturalWidth > maxWidthOrHeight || naturalHeight > maxWidthOrHeight) {
    var res = calcMaxSize(
      canvasWidth,
      canvasHeight,
      maxWidthOrHeight,
      maxWidthOrHeight
    );
    canvasWidth = res.width;
    canvasHeight = res.height;
  }
  logCompressImageDebug(
    '[compressImage] before drawImageWithOrientationToCanvas',
    {
      cost: new Date() - time,
      naturalWidth,
      naturalHeight,
      canvasWidth,
      canvasHeight,
      orientation,
      type,
      quality,
      maxWidthOrHeight
    }
  );

  // ---------- 8. 绘制到离屏 canvas（含 EXIF 方向变换） ----------
  var canvas = document.createElement('canvas');
  drawImageWithOrientationToCanvas(
    canvas,
    img,
    orientation,
    canvasWidth,
    canvasHeight,
    naturalWidth,
    naturalHeight
  );
  logCompressImageDebug(
    '[compressImage] after drawImageWithOrientationToCanvas',
    {
      cost: new Date() - time,
      canvasWidth: canvas.width,
      canvasHeight: canvas.height
    }
  );

  // ---------- 9. 导出 Blob 并生成临时 URL ----------
  logCompressImageDebug('[compressImage] before canvas.toBlob', {
    cost: new Date() - time,
    type,
    quality
  });
  var blob = await new Promise(function (resolve) {
    canvas.toBlob(resolve, type, quality);
  });
  logCompressImageDebug('[compressImage] after canvas.toBlob', {
    cost: new Date() - time,
    blobSize: blob && blob.size,
    blobType: blob && blob.type
  });
  var blobUrl = URL.createObjectURL(blob);
  var finishTime = new Date() - time;
  logTime('compressImage耗时:' + finishTime + 'ms');

  // ---------- 10. 超过 maxSizeMB 则降低 quality/尺寸并递归；否则延迟上报体积 ----------
  if (maxSizeMB && repeatTime < 3) {
    logCompressImageDebug('[compressImage] before blobUrlToFile result', {
      cost: new Date() - time,
      blobUrl
    });
    var file2 = await blobUrlToFile(blobUrl);
    var mb = file2.size / 1024 / 1024;
    logCompressImageDebug('[compressImage] after blobUrlToFile result', {
      cost: new Date() - time,
      fileSize: file2.size,
      mb,
      maxSizeMB,
      repeatTime
    });

    if (mb > maxSizeMB) {
      repeatTime++;
      quality = quality * 0.9;
      if (!keepWidthAndHeight) {
        maxWidthOrHeight = maxWidthOrHeight * 0.8;
      }
      options = Object.assign({}, options, {
        repeatTime: repeatTime,
        quality: quality,
        maxWidthOrHeight: maxWidthOrHeight
      });
      if (repeatTime > 1 && !keepOriginType) {
        options.customImageType = 'image/jpeg';
      }
      report('util_compressImage', {
        time: formatLogTime(finishTime),
        mb: formatFileSize(file2.size),
        msg:
          '图片大小为' +
          Math.round(mb) +
          'mb,超过' +
          maxSizeMB +
          'mb，进行第' +
          (repeatTime + 1) +
          '次压缩'
      });
      logCompressImageDebug('[compressImage] before recursive compressImage', {
        cost: new Date() - time,
        repeatTime,
        quality,
        maxWidthOrHeight,
        mb,
        maxSizeMB
      });
      return compressImage(imageSource, options);
    }
  } else {
    setTimeout(function () {
      blobUrlToFile(blobUrl).then(function (file) {
        report('util_compressImage', {
          time: formatLogTime(finishTime),
          mb: formatFileSize(file.size)
        });
      });
    }, 1000);
  }
  logCompressImageDebug('[compressImage] finish', {
    cost: new Date() - time,
    blobUrl
  });
  return blobUrl;
}
